import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  FormControl,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const statusMapping = {
  "To Start": "to start",
  Ongoing: "ongoing",
  Completed: "completed",
  Cancelled: "cancelled",
  "In Progress": "in progress",
  "On Hold": "on hold",
  "In Process": "in process",
  Done: "done",
};

const batchStatuses = [
  {
    id: 1,
    status_name: "To Start",
  },
  {
    id: 2,
    status_name: "On Hold",
  },
  {
    id: 3,
    status_name: "Completed",
  },
];

const EditBatch = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { batchId } = useParams();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState("success");

  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] =
    useState(true);

  const [loadingBranches, setLoadingBranches] =
    useState(false);

  const [loadingCourses, setLoadingCourses] =
    useState(false);

  const [loadingTrainers, setLoadingTrainers] =
    useState(false);

  // ----------------------------------------------------
  // TIME PICKER
  // ----------------------------------------------------

  const [openTimePicker, setOpenTimePicker] =
    useState(false);

  const [selectedStartTime, setSelectedStartTime] =
    useState("09:00");

  const [selectedEndTime, setSelectedEndTime] =
    useState("11:00");

  const [selectedAmPmStart, setSelectedAmPmStart] =
    useState("AM");

  const [selectedAmPmEnd, setSelectedAmPmEnd] =
    useState("AM");

  // ----------------------------------------------------
  // DROPDOWN DATA
  // ----------------------------------------------------

  const [courses, setCourses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [organizations, setOrganizations] =
    useState([]);
  const [allBranches, setAllBranches] =
    useState([]);

  const [selectedCourseDuration, setSelectedCourseDuration] =
    useState(null);

  // ----------------------------------------------------
  // FORM DATA
  // ----------------------------------------------------

  const [formData, setFormData] = useState({
    batch_name: "",
    course: "",
    trainer: "",
    start_date: "",
    completion_date: "",
    batch_time: "",
    batch_size: "",
    batch_status: "",
    remark: "",
    organization: "",
    branch: "",
  });

  const hours = Array.from(
    { length: 12 },
    (_, i) =>
      (i + 1).toString().padStart(2, "0")
  );

  const minutes = Array.from(
    { length: 60 },
    (_, i) =>
      i.toString().padStart(2, "0")
  );

  const amPmOptions = ["AM", "PM"];

  // ----------------------------------------------------
  // AUTH
  // ----------------------------------------------------

  const isAuthenticated = useCallback(() => {
    const token =
      localStorage.getItem("accessToken");

    return !!token;
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem("accessToken");

    return {
      Authorization: `Bearer ${token}`,
    };
  }, []);

  // ----------------------------------------------------
  // API ARRAY NORMALIZER
  // ----------------------------------------------------

  const getArrayData = (response) => {
    const data = response?.data;

    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.results)) {
      return data.results;
    }

    return [];
  };

  // ----------------------------------------------------
  // OBJECT ID HELPER
  // ----------------------------------------------------

  const getObjectId = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    if (typeof value === "object") {
      return (
        value?.id ??
        value?.pk ??
        value?.value ??
        ""
      ).toString();
    }

    return value.toString();
  };

  // ----------------------------------------------------
  // USER ORGANIZATION
  // ----------------------------------------------------

  const getUserOrganizationId = () => {
    try {
      const user = JSON.parse(
        sessionStorage.getItem("user") || "{}"
      );

      return (
        user?.organization_id ||
        user?.organization ||
        ""
      );
    } catch (error) {
      console.error(
        "Error parsing user session:",
        error
      );

      return "";
    }
  };

  // ----------------------------------------------------
  // FETCH BRANCHES
  // ----------------------------------------------------

  const fetchBranches = useCallback(
    async (organizationId) => {
      if (!organizationId) {
        setAllBranches([]);
        return [];
      }

      setLoadingBranches(true);

      try {
        const headers =
          getAuthHeaders();

        const response =
          await axios.get(
            `${API_BASE_URL}/api/master/organization/${encodeURIComponent(
              organizationId
            )}/branches/`,
            {
              headers,
            }
          );

        const branchList =
          getArrayData(response);

        console.log(
          "Branches fetched:",
          {
            organizationId,
            branchList,
          }
        );

        setAllBranches(
          branchList
        );

        return branchList;
      } catch (error) {
        console.error(
          "Error fetching branches:",
          error
        );

        setAllBranches([]);

        return [];
      } finally {
        setLoadingBranches(false);
      }
    },
    [getAuthHeaders]
  );

  // ----------------------------------------------------
  // FETCH COURSES
  // ----------------------------------------------------

  const fetchCourses = useCallback(
    async (
      organizationId,
      branchId
    ) => {
      if (
        !organizationId ||
        !branchId
      ) {
        setCourses([]);
        return [];
      }

      setLoadingCourses(true);

      try {
        const headers =
          getAuthHeaders();

        const response =
          await axios.get(
            `${API_BASE_URL}/api/course/courses/`,
            {
              params: {
                organization:
                  organizationId,
                branch:
                  branchId,
              },
              headers,
            }
          );

        let courseList =
          getArrayData(response);

        console.log(
          "Courses fetched:",
          {
            organizationId,
            branchId,
            courseList,
          }
        );

        /*
         * Keep selected organization/branch
         * courses when API supplies these fields.
         *
         * If the endpoint returns no organization/
         * branch metadata on a course, preserve
         * the API response.
         */

        courseList =
          courseList.filter(
            (course) => {
              const courseOrganization =
                course?.organization
                  ?.id ??
                course?.organization ??
                course?.organization_id ??
                null;

              const courseBranch =
                course?.branch?.id ??
                course?.branch ??
                course?.branch_id ??
                null;

              const organizationMatches =
                courseOrganization ===
                  null ||
                courseOrganization ===
                  undefined ||
                String(
                  courseOrganization
                ) ===
                  String(
                    organizationId
                  );

              const branchMatches =
                courseBranch ===
                  null ||
                courseBranch ===
                  undefined ||
                String(
                  courseBranch
                ) ===
                  String(
                    branchId
                  );

              return (
                organizationMatches &&
                branchMatches
              );
            }
          );

        console.log(
          "Filtered courses:",
          courseList
        );

        setCourses(courseList);

        return courseList;
      } catch (error) {
        console.error(
          "Error fetching courses:",
          error
        );

        setCourses([]);

        return [];
      } finally {
        setLoadingCourses(false);
      }
    },
    [getAuthHeaders]
  );

  // ----------------------------------------------------
  // FETCH TRAINERS
  // ----------------------------------------------------

  const fetchTrainers = useCallback(
    async (
      organizationId,
      branchId,
      courseId = ""
    ) => {
      if (
        !organizationId ||
        !branchId
      ) {
        setTrainers([]);
        return [];
      }

      setLoadingTrainers(true);

      try {
        const headers =
          getAuthHeaders();

        const response =
          await axios.get(
            `${API_BASE_URL}/api/staff/employees/filter/`,
            {
              params: {
                organization_id:
                  organizationId,
                branch_id:
                  branchId,
              },
              headers,
            }
          );

        let employeeList =
          getArrayData(response);

        console.log(
          "Employees for trainer filtering:",
          {
            organizationId,
            branchId,
            employeeList,
          }
        );

        let trainerEmployees =
          employeeList.filter(
            (employee) => {
              const designation =
                (
                  employee?.designation_name ||
                  employee?.designation
                    ?.name ||
                  employee?.designation ||
                  employee?.designationName ||
                  ""
                )
                  .toString()
                  .toLowerCase()
                  .trim();

              return (
                designation ===
                  "trainer" ||
                designation ===
                  "trainers" ||
                designation.includes(
                  "trainer"
                )
              );
            }
          );

        /*
         * If API provides branch/course context,
         * preserve selected branch/course.
         */

        trainerEmployees =
          trainerEmployees.filter(
            (employee) => {
              const employeeOrganization =
                employee?.organization
                  ?.id ??
                employee?.organization ??
                employee?.organization_id ??
                null;

              const employeeBranch =
                employee?.branch?.id ??
                employee?.branch ??
                employee?.branch_id ??
                null;

              const employeeCourse =
                employee?.course?.id ??
                employee?.course ??
                employee?.course_id ??
                null;

              const organizationMatches =
                employeeOrganization ===
                  null ||
                employeeOrganization ===
                  undefined ||
                String(
                  employeeOrganization
                ) ===
                  String(
                    organizationId
                  );

              const branchMatches =
                employeeBranch ===
                  null ||
                employeeBranch ===
                  undefined ||
                String(
                  employeeBranch
                ) ===
                  String(
                    branchId
                  );

              const courseMatches =
                !courseId ||
                employeeCourse ===
                  null ||
                employeeCourse ===
                  undefined ||
                String(
                  employeeCourse
                ) ===
                  String(
                    courseId
                  );

              return (
                organizationMatches &&
                branchMatches &&
                courseMatches
              );
            }
          );

        console.log(
          "Filtered trainers:",
          trainerEmployees
        );

        setTrainers(
          trainerEmployees
        );

        return trainerEmployees;
      } catch (error) {
        console.error(
          "Error fetching trainers:",
          error
        );

        setTrainers([]);

        return [];
      } finally {
        setLoadingTrainers(false);
      }
    },
    [getAuthHeaders]
  );

  // ----------------------------------------------------
  // AUTH REDIRECT
  // ----------------------------------------------------

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
    }
  }, [
    isAuthenticated,
    navigate,
  ]);

  // ----------------------------------------------------
  // FETCH EXISTING BATCH
  // ----------------------------------------------------

  useEffect(() => {
    let isMounted = true;

    const fetchBatchAndDropdownData =
      async () => {
        try {
          setLoadingData(true);

          const token =
            localStorage.getItem(
              "accessToken"
            );

          if (!token) {
            navigate("/login");
            return;
          }

          const headers =
            getAuthHeaders();

          const userOrganizationId =
            getUserOrganizationId();

          console.log(
            "User organization:",
            userOrganizationId
          );

          /*
           * Do NOT load all courses/
           * branches initially.
           *
           * First load the batch.
           * Batch gives organization
           * and branch.
           */

          const [
            batchRes,
            organizationsRes,
          ] = await Promise.all([
            axios.get(
              `${API_BASE_URL}/api/course/batches/${batchId}/`,
              {
                headers,
              }
            ),

            axios.get(
              `${API_BASE_URL}/api/master/organizations/`,
              {
                headers,
              }
            ),
          ]);

          if (!isMounted) {
            return;
          }

          console.log(
            "Batch response:",
            batchRes.data
          );

          console.log(
            "Organizations response:",
            organizationsRes.data
          );

          // ------------------------------------------------
          // ORGANIZATIONS
          // ------------------------------------------------

          const organizationList =
            getArrayData(
              organizationsRes
            );

          setOrganizations(
            organizationList
          );

          // ------------------------------------------------
          // BATCH DATA
          // ------------------------------------------------

          const batchData =
            batchRes.data?.data ||
            batchRes.data;

          console.log(
            "Actual batch data:",
            batchData
          );

          // ------------------------------------------------
          // TIME
          // ------------------------------------------------

          if (
            batchData?.batch_time
          ) {
            const timeParts =
              batchData.batch_time.split(
                " - "
              );

            if (
              timeParts.length ===
              2
            ) {
              const startPart =
                timeParts[0]
                  .trim()
                  .split(
                    " "
                  );

              const endPart =
                timeParts[1]
                  .trim()
                  .split(
                    " "
                  );

              if (
                startPart.length ===
                  2 &&
                endPart.length ===
                  2
              ) {
                const [
                  startTime,
                  startAmPm,
                ] = startPart;

                const [
                  endTime,
                  endAmPm,
                ] = endPart;

                setSelectedStartTime(
                  startTime
                );

                setSelectedAmPmStart(
                  startAmPm
                );

                setSelectedEndTime(
                  endTime
                );

                setSelectedAmPmEnd(
                  endAmPm
                );
              }
            }
          }

          // ------------------------------------------------
          // STATUS
          // ------------------------------------------------

          const batchStatusDisplay =
            batchStatuses.find(
              (status) =>
                status.status_name
                  ?.toLowerCase() ===
                batchData?.batch_status
                  ?.toLowerCase()
            )?.status_name ||
            batchData?.batch_status ||
            "";

          // ------------------------------------------------
          // ORGANIZATION
          // ------------------------------------------------

          const organizationValue =
            batchData?.organization
              ?.id ??
            batchData?.organization ??
            "";

          const batchOrganizationId =
            getObjectId(
              organizationValue
            );

          // ------------------------------------------------
          // BRANCH
          // ------------------------------------------------

          const branchValue =
            batchData?.branch
              ?.id ??
            batchData?.branch ??
            "";

          const batchBranchId =
            getObjectId(
              branchValue
            );

          // ------------------------------------------------
          // COURSE
          // ------------------------------------------------

          const batchCourseId =
            getObjectId(
              batchData?.course
            );

          // ------------------------------------------------
          // TRAINER
          // ------------------------------------------------

          const batchTrainerId =
            getObjectId(
              batchData?.trainer
            );

          // ------------------------------------------------
          // EFFECTIVE ORGANIZATION
          // ------------------------------------------------

          const effectiveOrganizationId =
            batchOrganizationId ||
            getObjectId(
              userOrganizationId
            );

          // ------------------------------------------------
          // INITIAL FORM
          // ------------------------------------------------

          setFormData({
            batch_name:
              batchData?.batch_name ||
              "",

            course:
              batchCourseId,

            trainer:
              batchTrainerId,

            start_date:
              batchData?.start_date ||
              "",

            completion_date:
              batchData?.completion_date ||
              "",

            batch_time:
              batchData?.batch_time ||
              "",

            batch_size:
              batchData?.batch_size
                ?.toString() ||
              "",

            batch_status:
              batchStatusDisplay,

            remark:
              batchData?.remark ||
              "",

            organization:
              effectiveOrganizationId,

            branch:
              batchBranchId,
          });

          // ------------------------------------------------
          // ORGANIZATION -> BRANCH
          // ------------------------------------------------

          if (
            effectiveOrganizationId
          ) {
            const branchList =
              await fetchBranches(
                effectiveOrganizationId
              );

            if (!isMounted) {
              return;
            }

            console.log(
              "Organization branches:",
              branchList
            );

            // Keep batch branch selected
            if (batchBranchId) {
              setFormData(
                (prev) => ({
                  ...prev,
                  organization:
                    effectiveOrganizationId,
                  branch:
                    batchBranchId,
                })
              );

              // --------------------------------------------
              // BRANCH -> COURSE
              // --------------------------------------------

              const courseList =
                await fetchCourses(
                  effectiveOrganizationId,
                  batchBranchId
                );

              if (!isMounted) {
                return;
              }

              console.log(
                "Branch courses:",
                courseList
              );

              // Keep existing course
              setFormData(
                (prev) => ({
                  ...prev,
                  course:
                    batchCourseId,
                })
              );

              // --------------------------------------------
              // COURSE -> TRAINER
              // --------------------------------------------

              const trainerList =
                await fetchTrainers(
                  effectiveOrganizationId,
                  batchBranchId,
                  batchCourseId
                );

              if (!isMounted) {
                return;
              }

              console.log(
                "Branch/course trainers:",
                trainerList
              );

              // Keep existing trainer
              setFormData(
                (prev) => ({
                  ...prev,
                  trainer:
                    batchTrainerId,
                })
              );
            } else {
              setCourses([]);
              setTrainers([]);
            }
          } else {
            setAllBranches([]);
            setCourses([]);
            setTrainers([]);
          }
        } catch (error) {
          console.error(
            "Error fetching batch data:",
            error
          );

          console.error(
            "Error response:",
            error?.response
          );

          if (
            error?.response?.status ===
            401
          ) {
            setSnackbarMessage(
              "Your session has expired. Please login again."
            );

            setSnackbarSeverity(
              "error"
            );

            setOpenSnackbar(
              true
            );

            setTimeout(() => {
              navigate("/login");
            }, 2000);

            return;
          }

          if (
            error?.response?.status ===
            404
          ) {
            setSnackbarMessage(
              "Batch not found. Please check the batch ID."
            );

            setSnackbarSeverity(
              "error"
            );

            setOpenSnackbar(
              true
            );

            setTimeout(() => {
              navigate("/batch");
            }, 2000);

            return;
          }

          let errorMessage =
            "Failed to load batch data. Please refresh the page.";

          if (
            error?.response
          ) {
            if (
              error.response.data
                ?.detail
            ) {
              errorMessage =
                error.response.data.detail;
            } else if (
              error.response.data
                ?.message
            ) {
              errorMessage =
                error.response.data.message;
            } else if (
              typeof error.response.data ===
              "string"
            ) {
              errorMessage =
                error.response.data;
            }
          } else if (
            error?.request
          ) {
            errorMessage =
              "No response from server. Please check your connection.";
          } else if (
            error?.message
          ) {
            errorMessage =
              error.message;
          }

          setSnackbarMessage(
            errorMessage
          );

          setSnackbarSeverity(
            "error"
          );

          setOpenSnackbar(
            true
          );
        } finally {
          if (isMounted) {
            setLoadingData(
              false
            );
          }
        }
      };

    if (
      isAuthenticated() &&
      batchId
    ) {
      fetchBatchAndDropdownData();
    }

    return () => {
      isMounted = false;
    };
  }, [
    batchId,
    navigate,
    isAuthenticated,
    getAuthHeaders,
    fetchBranches,
    fetchCourses,
    fetchTrainers,
  ]);

  // ----------------------------------------------------
  // COURSE DURATION
  // ----------------------------------------------------

  useEffect(() => {
    if (
      !formData.course ||
      !formData.start_date
    ) {
      setSelectedCourseDuration(
        null
      );

      return;
    }

    const selectedCourse =
      courses.find(
        (course) =>
          Number(course.id) ===
          Number(
            formData.course
          )
      );

    if (
      !selectedCourse?.course_duration
    ) {
      setSelectedCourseDuration(
        null
      );

      return;
    }

    const duration =
      selectedCourse.course_duration;

    setSelectedCourseDuration(
      duration
    );

    const durationStr =
      duration
        .toString()
        .toLowerCase();

    let durationValue = 0;
    let durationUnit =
      "months";

    const numberMatch =
      durationStr.match(
        /(\d+(?:\.\d+)?)/
      );

    if (numberMatch) {
      durationValue =
        parseFloat(
          numberMatch[1]
        );
    }

    if (
      durationStr.includes(
        "year"
      )
    ) {
      durationUnit =
        "years";
    } else if (
      durationStr.includes(
        "day"
      )
    ) {
      durationUnit =
        "days";
    } else {
      durationUnit =
        "months";
    }

    const startDate =
      new Date(
        formData.start_date
      );

    if (
      isNaN(
        startDate.getTime()
      )
    ) {
      return;
    }

    const completionDate =
      new Date(
        startDate
      );

    if (
      durationUnit ===
      "years"
    ) {
      completionDate.setMonth(
        completionDate.getMonth() +
          durationValue * 12
      );
    } else if (
      durationUnit ===
      "days"
    ) {
      completionDate.setDate(
        completionDate.getDate() +
          durationValue
      );
    } else {
      completionDate.setMonth(
        completionDate.getMonth() +
          durationValue
      );
    }

    const formattedDate =
      completionDate
        .toISOString()
        .split("T")[0];

    if (
      formData.completion_date !==
      formattedDate
    ) {
      setFormData(
        (prev) => ({
          ...prev,
          completion_date:
            formattedDate,
        })
      );
    }
  }, [
    formData.course,
    formData.start_date,
    courses,
  ]);

  // ----------------------------------------------------
  // TIME PICKER
  // ----------------------------------------------------

  const handleOpenTimePicker =
    () => {
      if (
        formData.batch_time
      ) {
        const timeParts =
          formData.batch_time.split(
            " - "
          );

        if (
          timeParts.length ===
          2
        ) {
          const startPart =
            timeParts[0]
              .trim()
              .split(
                " "
              );

          const endPart =
            timeParts[1]
              .trim()
              .split(
                " "
              );

          if (
            startPart.length ===
              2 &&
            endPart.length ===
              2
          ) {
            setSelectedStartTime(
              startPart[0]
            );

            setSelectedAmPmStart(
              startPart[1]
            );

            setSelectedEndTime(
              endPart[0]
            );

            setSelectedAmPmEnd(
              endPart[1]
            );
          }
        }
      }

      setOpenTimePicker(
        true
      );
    };

  const handleApplyTime =
    () => {
      const timeString =
        `${selectedStartTime} ${selectedAmPmStart} - ` +
        `${selectedEndTime} ${selectedAmPmEnd}`;

      setFormData(
        (prev) => ({
          ...prev,
          batch_time:
            timeString,
        })
      );

      setOpenTimePicker(
        false
      );
    };

  const handleCloseTimePicker =
    () => {
      setOpenTimePicker(
        false
      );
    };

  // ----------------------------------------------------
  // HANDLE CHANGE
  // ----------------------------------------------------

  const handleChange = async (
    e
  ) => {
    const {
      name,
      value,
    } = e.target;

    // -----------------------------------------------
    // START DATE
    // -----------------------------------------------

    if (
      name ===
      "start_date"
    ) {
      const newStartDate =
        value;

      if (
        formData.course
      ) {
        const selectedCourse =
          courses.find(
            (course) =>
              Number(
                course.id
              ) ===
              Number(
                formData.course
              )
          );

        if (
          selectedCourse?.course_duration
        ) {
          const durationStr =
            selectedCourse.course_duration
              .toString()
              .toLowerCase();

          const numberMatch =
            durationStr.match(
              /(\d+(?:\.\d+)?)/
            );

          const durationValue =
            numberMatch
              ? parseFloat(
                  numberMatch[1]
                )
              : 0;

          let durationUnit =
            "months";

          if (
            durationStr.includes(
              "year"
            )
          ) {
            durationUnit =
              "years";
          } else if (
            durationStr.includes(
              "day"
            )
          ) {
            durationUnit =
              "days";
          }

          const startDate =
            new Date(
              newStartDate
            );

          const completionDate =
            new Date(
              startDate
            );

          if (
            durationUnit ===
            "years"
          ) {
            completionDate.setMonth(
              completionDate.getMonth() +
                durationValue * 12
            );
          } else if (
            durationUnit ===
            "days"
          ) {
            completionDate.setDate(
              completionDate.getDate() +
                durationValue
            );
          } else {
            completionDate.setMonth(
              completionDate.getMonth() +
                durationValue
            );
          }

          const formattedDate =
            completionDate
              .toISOString()
              .split("T")[0];

          setFormData(
            (prev) => ({
              ...prev,
              start_date:
                newStartDate,
              completion_date:
                formattedDate,
            })
          );

          return;
        }
      }

      setFormData(
        (prev) => ({
          ...prev,
          start_date:
            newStartDate,
        })
      );

      return;
    }

    // -----------------------------------------------
    // BRANCH CHANGE
    // -----------------------------------------------

    if (
      name ===
      "branch"
    ) {
      const organizationId =
        formData.organization;

      // Clear dependent fields
      setFormData(
        (prev) => ({
          ...prev,
          branch:
            value,
          course: "",
          trainer: "",
        })
      );

      setCourses([]);
      setTrainers([]);

      if (
        organizationId &&
        value
      ) {
        const courseList =
          await fetchCourses(
            organizationId,
            value
          );

        console.log(
          "Branch changed:",
          {
            organizationId,
            branchId:
              value,
            courseList,
          }
        );
      }

      return;
    }

    // -----------------------------------------------
    // COURSE CHANGE
    // -----------------------------------------------

    if (
      name ===
      "course"
    ) {
      const organizationId =
        formData.organization;

      const branchId =
        formData.branch;

      setFormData(
        (prev) => ({
          ...prev,
          course:
            value,
          trainer: "",
        })
      );

      setTrainers([]);

      if (
        organizationId &&
        branchId &&
        value
      ) {
        await fetchTrainers(
          organizationId,
          branchId,
          value
        );
      }

      return;
    }

    // -----------------------------------------------
    // OTHER FIELDS
    // -----------------------------------------------

    setFormData(
      (prev) => ({
        ...prev,
        [name]:
          value,
      })
    );
  };

  // ----------------------------------------------------
  // VALIDATION
  // ----------------------------------------------------

  const validateForm =
    () => {
      const requiredFields =
        [
          "batch_name",
          "course",
          "trainer",
          "start_date",
          "completion_date",
          "batch_time",
          "batch_size",
          "batch_status",
          "organization",
          "branch",
        ];

      for (
        const field of requiredFields
      ) {
        if (
          !formData[field]
        ) {
          return `Please fill in ${field.replace(
            "_",
            " "
          )}`;
        }
      }

      // -----------------------------------------------
      // COURSE DURATION
      // -----------------------------------------------

      if (
        formData.course &&
        formData.start_date &&
        formData.completion_date
      ) {
        const selectedCourse =
          courses.find(
            (course) =>
              Number(
                course.id
              ) ===
              Number(
                formData.course
              )
          );

        if (
          selectedCourse?.course_duration
        ) {
          const durationStr =
            selectedCourse.course_duration
              .toString()
              .toLowerCase();

          const numberMatch =
            durationStr.match(
              /(\d+(?:\.\d+)?)/
            );

          const durationValue =
            numberMatch
              ? parseFloat(
                  numberMatch[1]
                )
              : 0;

          let durationUnit =
            "months";

          if (
            durationStr.includes(
              "year"
            )
          ) {
            durationUnit =
              "years";
          } else if (
            durationStr.includes(
              "day"
            )
          ) {
            durationUnit =
              "days";
          }

          const startDate =
            new Date(
              formData.start_date
            );

          const expectedDate =
            new Date(
              startDate
            );

          if (
            durationUnit ===
            "years"
          ) {
            expectedDate.setMonth(
              expectedDate.getMonth() +
                durationValue * 12
            );
          } else if (
            durationUnit ===
            "days"
          ) {
            expectedDate.setDate(
              expectedDate.getDate() +
                durationValue
            );
          } else {
            expectedDate.setMonth(
              expectedDate.getMonth() +
                durationValue
            );
          }

          const expectedDateStr =
            expectedDate
              .toISOString()
              .split("T")[0];

          if (
            expectedDateStr !==
            formData.completion_date
          ) {
            return (
              `Batch duration must match course duration ` +
              `(${selectedCourse.course_duration}). ` +
              `Expected completion date: ${expectedDateStr}`
            );
          }
        }
      }

      // -----------------------------------------------
      // DATE VALIDATION
      // -----------------------------------------------

      const startDate =
        new Date(
          formData.start_date
        );

      const completionDate =
        new Date(
          formData.completion_date
        );

      if (
        completionDate <
        startDate
      ) {
        return "Completion date cannot be before start date";
      }

      // -----------------------------------------------
      // BATCH SIZE
      // -----------------------------------------------

      const batchSize =
        parseInt(
          formData.batch_size,
          10
        );

      if (
        isNaN(batchSize) ||
        batchSize <= 0
      ) {
        return "Batch size must be a positive number";
      }

      return null;
    };

  // ----------------------------------------------------
  // SAVE
  // ----------------------------------------------------

  const handleSave =
    async (e) => {
      e.preventDefault();

      if (
        !isAuthenticated()
      ) {
        setSnackbarMessage(
          "Please login to continue"
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );

        navigate(
          "/login"
        );

        return;
      }

      const validationError =
        validateForm();

      if (
        validationError
      ) {
        setSnackbarMessage(
          validationError
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );

        return;
      }

      setLoading(true);

      try {
        const payload = {
          batch_name:
            formData.batch_name,

          course:
            parseInt(
              formData.course,
              10
            ),

          trainer:
            parseInt(
              formData.trainer,
              10
            ),

          start_date:
            formData.start_date,

          completion_date:
            formData.completion_date,

          batch_time:
            formData.batch_time,

          batch_size:
            parseInt(
              formData.batch_size,
              10
            ),

          batch_status:
            statusMapping[
              formData.batch_status
            ] ||
            formData.batch_status
              .toLowerCase(),

          remark:
            formData.remark ||
            "",

          organization:
            parseInt(
              formData.organization,
              10
            ),

          branch:
            parseInt(
              formData.branch,
              10
            ),
        };

        console.log(
          "Updating batch with payload:",
          payload
        );

        const headers =
          getAuthHeaders();

        const response =
          await axios.put(
            `${API_BASE_URL}/api/course/batches/${batchId}/`,
            payload,
            {
              headers,
            }
          );

        console.log(
          "Update response:",
          response.data
        );

        if (
          response.status ===
            200 ||
          response.status ===
            202
        ) {
          setShowSuccessModal(
            true
          );
        } else {
          throw new Error(
            "Failed to update batch"
          );
        }
      } catch (error) {
        console.error(
          "Error updating batch:",
          error
        );

        console.error(
          "Error response:",
          error?.response
        );

        if (
          error?.response?.status ===
          401
        ) {
          setSnackbarMessage(
            "Your session has expired. Please login again."
          );

          setSnackbarSeverity(
            "error"
          );

          setOpenSnackbar(
            true
          );

          setTimeout(() => {
            navigate(
              "/login"
            );
          }, 2000);

          return;
        }

        let errorMessage =
          "Failed to update batch. Please try again.";

        const errorData =
          error?.response?.data;

        if (
          errorData
        ) {
          if (
            typeof errorData ===
            "object"
          ) {
            if (
              errorData.detail
            ) {
              errorMessage =
                errorData.detail;
            } else if (
              errorData.message
            ) {
              errorMessage =
                errorData.message;
            } else if (
              Array.isArray(
                errorData
              )
            ) {
              errorMessage =
                errorData.join(
                  ", "
                );
            } else if (
              errorData.batch_status
            ) {
              errorMessage =
                `Batch Status Error: ${
                  Array.isArray(
                    errorData.batch_status
                  )
                    ? errorData.batch_status.join(
                        ", "
                      )
                    : errorData.batch_status
                }`;
            } else {
              const firstKey =
                Object.keys(
                  errorData
                )[0];

              if (
                firstKey
              ) {
                const firstValue =
                  errorData[
                    firstKey
                  ];

                errorMessage =
                  `${firstKey}: ${
                    Array.isArray(
                      firstValue
                    )
                      ? firstValue.join(
                          ", "
                        )
                      : firstValue
                  }`;
              }
            }
          } else if (
            typeof errorData ===
            "string"
          ) {
            errorMessage =
              errorData;
          }
        }

        setSnackbarMessage(
          errorMessage
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  // ----------------------------------------------------
  // CANCEL
  // ----------------------------------------------------

  const handleCancel =
    () => {
      navigate(-1);
    };

  // ----------------------------------------------------
  // TOOLTIP
  // ----------------------------------------------------

  const getTooltipMessage =
    () => {
      const missingFields =
        [];

      if (
        !formData.batch_name
      ) {
        missingFields.push(
          "Enter Batch Name"
        );
      }

      if (
        !formData.batch_size
      ) {
        missingFields.push(
          "Enter Batch Size"
        );
      }

      if (
        !formData.course
      ) {
        missingFields.push(
          "Select Course"
        );
      }

      if (
        !formData.trainer
      ) {
        missingFields.push(
          "Select Trainer"
        );
      }

      if (
        !formData.batch_status
      ) {
        missingFields.push(
          "Select Batch Status"
        );
      }

      if (
        !formData.start_date
      ) {
        missingFields.push(
          "Select Start Date"
        );
      }

      if (
        !formData.completion_date
      ) {
        missingFields.push(
          "Select Completion Date"
        );
      }

      if (
        !formData.batch_time
      ) {
        missingFields.push(
          "Select Batch Time"
        );
      }

      if (
        !formData.organization
      ) {
        missingFields.push(
          "Select Organization"
        );
      }

      if (
        !formData.branch
      ) {
        missingFields.push(
          "Select Branch"
        );
      }

      if (
        loadingBranches
      ) {
        missingFields.push(
          "Loading Branches"
        );
      }

      if (
        loadingCourses
      ) {
        missingFields.push(
          "Loading Courses"
        );
      }

      if (
        loadingTrainers
      ) {
        missingFields.push(
          "Loading Trainers"
        );
      }

      return missingFields.join(
        "\n"
      );
    };

  // ----------------------------------------------------
  // AUTH CHECK
  // ----------------------------------------------------

  if (
    !isAuthenticated()
  ) {
    return null;
  }

  // ----------------------------------------------------
  // LOADING
  // ----------------------------------------------------

  if (
    loadingData
  ) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          my: 4,
          display: "flex",
          justifyContent:
            "center",
          alignItems:
            "center",
          minHeight:
            "400px",
        }}
      >
        <Box
          sx={{
            textAlign:
              "center",
          }}
        >
          <CircularProgress />

          <Typography
            sx={{
              mt: 2,
            }}
          >
            Loading batch data...
          </Typography>
        </Box>
      </Container>
    );
  }

  // ----------------------------------------------------
  // UI
  // ----------------------------------------------------

  return (
    <Container
      maxWidth="lg"
      sx={{ my: 0 }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette
              .background.paper,
        }}
      >
        {/* HEADER */}

        <Box
          sx={{
            display:
              "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight:
                "bold",
              color:
                theme.palette
                  .primary.main,
            }}
          >
            Edit Batch
          </Typography>
        </Box>

        <form
          onSubmit={
            handleSave
          }
        >
          {/* ----------------------------------------- */}
          {/* ORGANIZATION + BRANCH */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display:
                "flex",
              gap: 2,
              mb: 3,
            }}
          >
            {/* ORGANIZATION */}

            <FormControl
              fullWidth
              required
              disabled
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,

                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Organization *
              </InputLabel>

              <Select
                name="organization"
                value={
                  formData.organization
                }
                label="Organization *"
                disabled
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "&.Mui-disabled .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .action
                          .disabled,
                    },
                }}
              >
                {organizations.map(
                  (
                    organization
                  ) => (
                    <MenuItem
                      key={
                        organization.id
                      }
                      value={String(
                        organization.id
                      )}
                    >
                      {organization.name ||
                        organization.organization_name ||
                        `Organization ${organization.id}`}
                    </MenuItem>
                  )
                )}
              </Select>

              {formData.organization && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color:
                      theme
                        .palette
                        .info
                        .main,
                  }}
                >
                  Auto-selected from batch
                </Typography>
              )}
            </FormControl>

            {/* BRANCH */}

            <FormControl
              fullWidth
              required
              disabled
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,

                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Branch *
              </InputLabel>

              <Select
              disabled
                name="branch"
                value={
                  formData.branch
                }
                label="Branch *"
                onChange={
                  handleChange
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {allBranches.map(
                  (branch) => (
                    <MenuItem
                      key={
                        branch.id
                      }
                      value={String(
                        branch.id
                      )}
                    >
                      {branch.name ||
                        branch.branch_name ||
                        branch.branchName ||
                        `Branch ${branch.id}`}
                    </MenuItem>
                  )
                )}
              </Select>

              {formData.branch && (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color:
                      theme
                        .palette
                        .info
                        .main,
                  }}
                >
                  Select branch to load related courses
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* ----------------------------------------- */}
          {/* BATCH NAME + COURSE */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display:
                "flex",
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              label="Batch Name"
              variant="outlined"
              fullWidth
              name="batch_name"
              value={
                formData.batch_name
              }
              onChange={
                handleChange
              }
              required
              disabled={
                loading
              }
              sx={{
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            <FormControl
              fullWidth
              required
              disabled={
                loading ||
                loadingCourses ||
                !formData.branch ||
                courses.length ===
                  0
              }
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,

                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Course
              </InputLabel>

              <Select
                name="course"
                value={
                  formData.course
                }
                label="Course"
                onChange={
                  handleChange
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {courses.map(
                  (course) => (
                    <MenuItem
                      key={
                        course.id
                      }
                      value={String(
                        course.id
                      )}
                    >
                      {course.name ||
                        course.course_name ||
                        `Course ${course.id}`}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {/* ----------------------------------------- */}
          {/* TRAINER + TIME */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display:
                "flex",
              gap: 2,
              mb: 3,
            }}
          >
            <FormControl
              fullWidth
              required
              disabled={
                loading ||
                loadingTrainers ||
                !formData.course ||
                trainers.length ===
                  0
              }
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,

                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Trainer
              </InputLabel>

              <Select
                name="trainer"
                value={
                  formData.trainer
                }
                label="Trainer"
                onChange={
                  handleChange
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {trainers.map(
                  (trainer) => (
                    <MenuItem
                      key={
                        trainer.id
                      }
                      value={String(
                        trainer.id
                      )}
                    >
                      {trainer.name ||
                        trainer.employee_name ||
                        `${trainer.first_name || ""} ${
                          trainer.last_name || ""
                        }`.trim() ||
                        `Trainer ${trainer.id}`}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              required
              disabled={
                loading
              }
            >
              <TextField
                label="Batch Time"
                variant="outlined"
                fullWidth
                name="batch_time"
                value={
                  formData.batch_time
                }
                onClick={
                  handleOpenTimePicker
                }
                required
                disabled={
                  loading
                }
                InputProps={{
                  readOnly:
                    true,

                  endAdornment:
                    (
                      <IconButton
                        onClick={
                          handleOpenTimePicker
                        }
                        disabled={
                          loading
                        }
                        edge="end"
                      >
                        <AccessTimeIcon />
                      </IconButton>
                    ),
                }}
                sx={{
                  "& .MuiInputLabel-root":
                    {
                      color:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "& .MuiInputLabel-root.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },

                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },

                  cursor:
                    "pointer",
                }}
              />
            </FormControl>
          </Box>

          {/* ----------------------------------------- */}
          {/* START + COMPLETION DATE */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display:
                "flex",
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              fullWidth
              name="start_date"
              value={
                formData.start_date
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink:
                  true,
              }}
              required
              disabled={
                loading
              }
              sx={{
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator":
                  {
                    filter:
                      theme
                        .palette
                        .mode ===
                      "dark"
                        ? "invert(1)"
                        : "none",
                  },
              }}
            />

            <TextField
              label="Completion Date"
              type="date"
              variant="outlined"
              fullWidth
              name="completion_date"
              value={
                formData.completion_date
              }
              onChange={
                handleChange
              }
              InputLabelProps={{
                shrink:
                  true,
              }}
              required
              disabled={
                loading
              }
              sx={{
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiInputBase-input[type='date']::-webkit-calendar-picker-indicator":
                  {
                    filter:
                      theme
                        .palette
                        .mode ===
                      "dark"
                        ? "invert(1)"
                        : "none",
                  },
              }}
            />
          </Box>

          {/* ----------------------------------------- */}
          {/* DURATION INFO */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              mb: 3,
              px: 1,
            }}
          >
            {selectedCourseDuration && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    theme
                      .palette
                      .info
                      .main,
                  display:
                    "block",
                  mb: 0.5,
                }}
              >
                ℹ️ Course Duration:{" "}
                {
                  selectedCourseDuration
                }
                . Completion date must
                match the course duration.
              </Typography>
            )}

            {formData.start_date &&
              formData.completion_date &&
              selectedCourseDuration && (
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme
                        .palette
                        .warning
                        .main,
                    display:
                      "block",
                  }}
                >
                  ⚠️ Changing the start
                  date will automatically
                  adjust the completion
                  date.
                </Typography>
              )}
          </Box>

          {/* ----------------------------------------- */}
          {/* SIZE + STATUS */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display:
                "flex",
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              label="Batch Size"
              type="number"
              variant="outlined"
              fullWidth
              name="batch_size"
              value={
                formData.batch_size
              }
              onChange={
                handleChange
              }
              InputProps={{
                inputProps:
                  {
                    min: 1,
                  },
              }}
              required
              disabled={
                loading
              }
              sx={{
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />

            <FormControl
              fullWidth
              required
              disabled={
                loading
              }
            >
              <InputLabel
                sx={{
                  color:
                    theme
                      .palette
                      .text
                      .primary,

                  "&.Mui-focused":
                    {
                      color:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                Batch Status
              </InputLabel>

              <Select
                name="batch_status"
                value={
                  formData.batch_status
                }
                label="Batch Status"
                onChange={
                  handleChange
                }
                sx={{
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .text
                          .primary,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme
                          .palette
                          .primary
                          .main,
                    },
                }}
              >
                {batchStatuses.map(
                  (status) => (
                    <MenuItem
                      key={
                        status.id
                      }
                      value={
                        status.status_name
                      }
                    >
                      {
                        status.status_name
                      }
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>

          {/* ----------------------------------------- */}
          {/* REMARK */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              mb: 3,
            }}
          >
            <TextField
              label="Remark"
              variant="outlined"
              fullWidth
              name="remark"
              value={
                formData.remark
              }
              onChange={
                handleChange
              }
              multiline
              rows={4}
              disabled={
                loading
              }
              sx={{
                "& .MuiInputLabel-root":
                  {
                    color:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "& .MuiInputLabel-root.Mui-focused":
                  {
                    color:
                      theme
                        .palette
                        .primary
                        .main,
                  },

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .text
                        .primary,
                  },

                "&:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor:
                      theme
                        .palette
                        .primary
                        .main,
                  },
              }}
            />
          </Box>

          {/* ----------------------------------------- */}
          {/* BUTTONS */}
          {/* ----------------------------------------- */}

          <Box
            sx={{
              display:
                "flex",
              justifyContent:
                "flex-end",
              gap: 2,
            }}
          >
            <Tooltip
              title={
                getTooltipMessage()
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    loading ||
                    loadingBranches ||
                    loadingCourses ||
                    loadingTrainers ||
                    !formData.batch_name ||
                    !formData.batch_size ||
                    !formData.course ||
                    !formData.trainer ||
                    !formData.batch_status ||
                    !formData.start_date ||
                    !formData.completion_date ||
                    !formData.batch_time ||
                    !formData.organization ||
                    !formData.branch
                  }
                  startIcon={
                    loading ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                      />
                    ) : null
                  }
                >
                  {loading
                    ? "UPDATING..."
                    : "UPDATE"}
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              color="error"
              onClick={
                handleCancel
              }
              disabled={
                loading
              }
            >
              CANCEL
            </Button>
          </Box>
        </form>

        {/* ================================================= */}
        {/* TIME PICKER */}
        {/* ================================================= */}

        <Dialog
          open={
            openTimePicker
          }
          onClose={
            handleCloseTimePicker
          }
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              pb: 1,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h6"
              >
                Select Batch Time
              </Typography>

              <IconButton
                onClick={
                  handleCloseTimePicker
                }
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent
            sx={{
              pt: 1,
            }}
          >
            {/* START TIME */}

            <Typography
              variant="subtitle2"
              gutterBottom
              color="primary"
            >
              Start Time
            </Typography>

            <Grid
              container
              spacing={1}
              alignItems="center"
            >
              <Grid
                item
                xs={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Hour
                  </InputLabel>

                  <Select
                    value={
                      selectedStartTime.split(
                        ":"
                      )[0]
                    }
                    onChange={(
                      e
                    ) => {
                      const newHour =
                        e.target.value;

                      const currentMinute =
                        selectedStartTime.split(
                          ":"
                        )[1] ||
                        "00";

                      setSelectedStartTime(
                        `${newHour}:${currentMinute}`
                      );
                    }}
                  >
                    {hours.map(
                      (
                        hour
                      ) => (
                        <MenuItem
                          key={`start-hour-${hour}`}
                          value={
                            hour
                          }
                        >
                          {hour}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Minute
                  </InputLabel>

                  <Select
                    value={
                      selectedStartTime.split(
                        ":"
                      )[1] ||
                      "00"
                    }
                    onChange={(
                      e
                    ) => {
                      const newMinute =
                        e.target.value;

                      const currentHour =
                        selectedStartTime.split(
                          ":"
                        )[0] ||
                        "09";

                      setSelectedStartTime(
                        `${currentHour}:${newMinute}`
                      );
                    }}
                  >
                    {minutes.map(
                      (
                        minute
                      ) => (
                        <MenuItem
                          key={`start-minute-${minute}`}
                          value={
                            minute
                          }
                        >
                          {
                            minute
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <Select
                    value={
                      selectedAmPmStart
                    }
                    onChange={(
                      e
                    ) =>
                      setSelectedAmPmStart(
                        e.target.value
                      )
                    }
                  >
                    {amPmOptions.map(
                      (
                        ampm
                      ) => (
                        <MenuItem
                          key={`start-${ampm}`}
                          value={
                            ampm
                          }
                        >
                          {
                            ampm
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* END TIME */}

            <Typography
              variant="subtitle2"
              gutterBottom
              color="primary"
              sx={{
                mt: 2,
              }}
            >
              End Time
            </Typography>

            <Grid
              container
              spacing={1}
              alignItems="center"
            >
              <Grid
                item
                xs={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Hour
                  </InputLabel>

                  <Select
                    value={
                      selectedEndTime.split(
                        ":"
                      )[0]
                    }
                    onChange={(
                      e
                    ) => {
                      const newHour =
                        e.target.value;

                      const currentMinute =
                        selectedEndTime.split(
                          ":"
                        )[1] ||
                        "00";

                      setSelectedEndTime(
                        `${newHour}:${currentMinute}`
                      );
                    }}
                  >
                    {hours.map(
                      (
                        hour
                      ) => (
                        <MenuItem
                          key={`end-hour-${hour}`}
                          value={
                            hour
                          }
                        >
                          {
                            hour
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <InputLabel>
                    Minute
                  </InputLabel>

                  <Select
                    value={
                      selectedEndTime.split(
                        ":"
                      )[1] ||
                      "00"
                    }
                    onChange={(
                      e
                    ) => {
                      const newMinute =
                        e.target.value;

                      const currentHour =
                        selectedEndTime.split(
                          ":"
                        )[0] ||
                        "11";

                      setSelectedEndTime(
                        `${currentHour}:${newMinute}`
                      );
                    }}
                  >
                    {minutes.map(
                      (
                        minute
                      ) => (
                        <MenuItem
                          key={`end-minute-${minute}`}
                          value={
                            minute
                          }
                        >
                          {
                            minute
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={4}
              >
                <FormControl
                  fullWidth
                  size="small"
                >
                  <Select
                    value={
                      selectedAmPmEnd
                    }
                    onChange={(
                      e
                    ) =>
                      setSelectedAmPmEnd(
                        e.target.value
                      )
                    }
                  >
                    {amPmOptions.map(
                      (
                        ampm
                      ) => (
                        <MenuItem
                          key={`end-${ampm}`}
                          value={
                            ampm
                          }
                        >
                          {
                            ampm
                          }
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* PREVIEW */}

            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor:
                  "primary.light",
                borderRadius:
                  1,
                textAlign:
                  "center",
              }}
            >
              <Typography
                variant="body2"
                color="primary.contrastText"
              >
                {
                  selectedStartTime
                }{" "}
                {
                  selectedAmPmStart
                } -{" "}
                {
                  selectedEndTime
                }{" "}
                {
                  selectedAmPmEnd
                }
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={
                handleCloseTimePicker
              }
              color="secondary"
              size="small"
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              onClick={
                handleApplyTime
              }
              variant="contained"
              color="primary"
              size="small"
            >
              Apply Time
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================================================= */}
        {/* SUCCESS MODAL */}
        {/* ================================================= */}

        <Modal
          open={
            showSuccessModal
          }
          onClose={() =>
            setShowSuccessModal(
              false
            )
          }
          BackdropProps={{
            style: {
              backgroundColor:
                "rgba(0, 0, 0, 0.8)",
            },
          }}
        >
          <Box
            sx={{
              position:
                "absolute",
              top: "50%",
              left: "50%",
              transform:
                "translate(-50%, -50%)",
              width: 400,
              bgcolor:
                theme.palette
                  .background
                  .paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow:
                "hidden",
            }}
          >
            <Box
              sx={{
                backgroundColor:
                  theme
                    .palette
                    .primary
                    .main,
                padding:
                  "12px 16px",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color:
                    theme
                      .palette
                      .primary
                      .contrastText,
                }}
              >
                Batch Updated ✅
              </Typography>
            </Box>

            <Box
              sx={{
                padding:
                  "20px",
              }}
            >
              <Typography
                sx={{
                  mb: 2,
                }}
              >
                Batch details have been
                updated successfully.
              </Typography>

              <Box
                sx={{
                  display:
                    "flex",
                  justifyContent:
                    "flex-end",
                  mt: 3,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(
                      false
                    );

                    navigate(-1);
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>

      {/* ================================================= */}
      {/* SNACKBAR */}
      {/* ================================================= */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical:
            "top",
          horizontal:
            "center",
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: "100%",
            whiteSpace:
              "pre-line",
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditBatch;